"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Icon20ArrowRightOutline } from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { BlurFade } from "@/components/magicui/blur-fade";
import { MaskReveal } from "@/components/motion/mask-reveal";
import { RotatingText } from "@/components/reactbits/rotating-text";
import { Magnet } from "@/components/reactbits/magnet";
import { Aurora } from "@/components/reactbits/aurora";
import { SwipeDeck } from "@/components/motion/swipe-deck";
import { scrollToId } from "@/lib/scroll";
import { Telemetry, TelemetryEvent } from "@linqx/telemetry";

export function Hero() {
  const ref = useRef<HTMLElement>(null);
  // Drive a parallax "lift-off" as the hero scrolls away.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const deckY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const deckRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);

  // Track the CTA that drove the jump to the waitlist, then scroll to it.
  const scrollToWaitlist = (cta: string) => {
    Telemetry.track(TelemetryEvent.CTA_CLICKED, { location: "hero", cta });
    scrollToId("waitlist");
  };

  return (
    <section
      ref={ref}
      className="relative lg:min-h-screen flex items-center overflow-hidden pt-28 pb-20 lg:pt-24 lg:pb-16 bg-background"
    >
      <Aurora
        colorStops={["#D4A5A5", "#C47979", "#D4A5A5"]}
        amplitude={1.0}
        blend={1.2}
        speed={0.5}
      />

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background pointer-events-none z-10" />

      <div className="relative z-10 container mx-auto px-4 grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
        {/* Left — kinetic copy */}
        <motion.div
          style={{ y: textY, opacity: textOpacity }}
          className="text-center lg:text-left"
        >
          <BlurFade delay={0.1} inView>
            <p className="text-base md:text-lg font-semibold text-brand-600 mb-4">
              {Language.getKey("landing_hero_eyebrow")}
            </p>
          </BlurFade>

          <div className="flex flex-col items-center lg:items-start mb-5">
            <h1 className="text-5xl md:text-7xl lg:text-7xl xl:text-8xl font-heading font-bold tracking-tight text-brand-900">
              <MaskReveal duration={1} delay={0.15} immediate>
                {Language.getKey("landing_hero_title")}
              </MaskReveal>
            </h1>
          </div>

          <BlurFade delay={0.4} inView>
            <div className="text-lg md:text-xl text-brand-700 max-w-xl mx-auto lg:mx-0 mb-3 flex flex-wrap justify-center lg:justify-start gap-x-2">
              <span>{Language.getKey("landing_hero_meet_prefix")}</span>
              <RotatingText
                texts={[
                  Language.getKey("landing_hero_rotating_adventure"),
                  Language.getKey("landing_hero_rotating_nightlife"),
                  Language.getKey("landing_hero_rotating_coffee"),
                  Language.getKey("landing_hero_rotating_events"),
                  Language.getKey("landing_hero_rotating_experiences"),
                ]}
                mainClassName="inline-flex text-brand-500 font-semibold overflow-hidden"
                staggerFrom="first"
                staggerDuration={0.02}
                rotationInterval={2500}
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
              />
            </div>
            <p className="text-base md:text-lg text-brand-700 max-w-xl mx-auto lg:mx-0 mb-8">
              {Language.getKey("landing_hero_subtitle")}{" "}
              <span className="text-brand-900 font-medium">
                {Language.getKey("landing_hero_subtitle_emphasis")}
              </span>
            </p>
          </BlurFade>

          <BlurFade delay={0.5} inView>
            <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4">
              <Magnet padding={60} magnetStrength={2.5}>
                <button
                  onClick={() => scrollToWaitlist("match")}
                  className="btn-brand h-14 px-10 text-lg font-semibold rounded-full flex items-center gap-2 group shadow-lg shadow-brand-500/30"
                >
                  {Language.getKey("landing_hero_cta_match")}
                  <Icon20ArrowRightOutline className="group-hover:translate-x-1 transition-transform" />
                </button>
              </Magnet>
              <Magnet padding={50} magnetStrength={3}>
                <button
                  onClick={() => scrollToWaitlist("create_profile")}
                  className="h-14 px-8 text-base font-semibold rounded-full flex items-center gap-2 border-2 border-brand-400 text-brand-700 hover:border-brand-700 transition-colors"
                >
                  {Language.getKey("landing_hero_cta_create_profile")}
                </button>
              </Magnet>
            </div>
          </BlurFade>

          <BlurFade delay={0.6} inView>
            <p className="mt-6 text-sm text-brand-500 text-center lg:text-left">
              <span className="lg:hidden">
                {Language.getKey("landing_hero_hint_mobile")}
              </span>
              <span className="hidden lg:inline">
                {Language.getKey("landing_hero_hint_desktop")}
              </span>
              {Language.getKey("landing_hero_hint_confirm")}
            </p>
          </BlurFade>
        </motion.div>

        {/* Right — interactive swipe deck */}
        <motion.div
          style={{ y: deckY, rotate: deckRotate }}
          className="flex justify-center lg:justify-end"
        >
          <SwipeDeck />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <BlurFade delay={0.8} inView>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
          <span className="text-xs text-brand-500">
            {Language.getKey("landing_hero_scroll_indicator")}
          </span>
          <div className="w-6 h-10 border-2 border-brand-400/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-brand-500 rounded-full animate-bounce" />
          </div>
        </div>
      </BlurFade>
    </section>
  );
}
